import React, { useEffect, useState } from "react";
import axios from "../axios";
import dayjs from "dayjs";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import "../styles/admin.css";

export default function AdminUserPostsPage({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [posts, setPosts] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1 });
  const [filters, setFilters] = useState({ from: "", to: "" });
  const [draftFilters, setDraftFilters] = useState({ from: "", to: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const today = dayjs();
    setDraftFilters({
      from: today.subtract(1, "day").format("YYYY-MM-DD"),
      to: today.format("YYYY-MM-DD"),
    });
  }, []);

  useEffect(() => {
    if (currentUser?.user_role_id === 1) {
      axios.get("/users").then((res) => {
        setUsers(res.data);
        if (res.data.length > 0) setSelectedUserId(res.data[0].id);
      });
    } else {
      setSelectedUserId(currentUser?.id);
    }
  }, [currentUser]);

  const fetchPosts = (page = 1) => {
    if (!selectedUserId) return;
    setLoading(true);
    axios
      .get(`/admin/users/${selectedUserId}/posts`, { params: { ...filters, page } })
      .then((res) => {
        setPosts(res.data.sightings.data);
        setPagination({
          current_page: res.data.sightings.current_page,
          last_page: res.data.sightings.last_page,
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPosts();
  }, [selectedUserId, filters]);

  const handleDelete = async (postId) => {
    if (!window.confirm("本当に削除しますか？")) return;
    try {
      await axios.delete(`/admin/posts/${postId}`);
      setPosts((prev) => prev.filter((p) => p.id !== postId));
    } catch {
      alert("削除に失敗しました");
    }
  };

  const applyFilters = () => setFilters({ ...draftFilters });
  const selectedUser = users.find((u) => String(u.id) === String(selectedUserId));

  return (
    <div className="adm-page">
      <AdminBreadcrumb items={[{ label: "ユーザ別投稿ログ" }]} />

      <div className="adm-header">
        <div className="adm-header-left">
          <h2 className="adm-title">ユーザ別投稿ログ</h2>
          <span className="adm-badge adm-badge--green">管理者</span>
        </div>
      </div>

      {/* フィルター */}
      <div className="adm-filter-box">
        <div className="adm-filter-row">
          {currentUser?.user_role_id === 1 && (
            <div className="adm-filter-field">
              <span className="adm-filter-label">ユーザ</span>
              <select
                className="adm-select"
                value={selectedUserId || ""}
                onChange={(e) => setSelectedUserId(e.target.value)}
                style={{ minWidth: 160 }}
              >
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}（ID: {u.id}）
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="adm-filter-field">
            <span className="adm-filter-label">開始日</span>
            <input
              type="date"
              className="adm-input"
              value={draftFilters.from}
              onChange={(e) => setDraftFilters({ ...draftFilters, from: e.target.value })}
            />
          </div>
          <div className="adm-filter-field">
            <span className="adm-filter-label">終了日</span>
            <input
              type="date"
              className="adm-input"
              value={draftFilters.to}
              onChange={(e) => setDraftFilters({ ...draftFilters, to: e.target.value })}
            />
          </div>
          <button className="adm-btn adm-btn--primary" onClick={applyFilters}>
            適用
          </button>
        </div>
      </div>

      {/* テーブル */}
      {loading ? (
        <div className="adm-empty">読み込み中…</div>
      ) : (
        <div className="adm-card">
          <div className="adm-card__header">
            {selectedUser ? `${selectedUser.name} の投稿` : "投稿一覧"}
            <span className="adm-card__header-sub">
              {pagination.current_page} / {pagination.last_page} ページ
            </span>
          </div>
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  {["投稿日時", "営業所", "路線", "車号", "始発地", "コメント", "操作"].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {posts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="adm-empty">該当する投稿がありません</td>
                  </tr>
                ) : (
                  posts.map((p) => (
                    <tr key={p.id}>
                      <td className="adm-nowrap adm-table__mute">{p.posted_at}</td>
                      <td>
                        <span className="adm-badge adm-badge--blue">{p.depot_short}</span>
                      </td>
                      <td className="adm-table__mute">{p.route_short}</td>
                      <td className="adm-table__bold">{p.bus_number}</td>
                      <td>{p.start}</td>
                      <td className="adm-table__ellipsis adm-table__mute">
                        {p.comment || <span style={{ color: "var(--adm-text-muted)" }}>(なし)</span>}
                      </td>
                      <td>
                        <button
                          className="adm-btn adm-btn--danger adm-btn--sm"
                          onClick={() => handleDelete(p.id)}
                        >
                          削除
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ページネーション */}
          <div className="adm-pagination">
            <button
              className="adm-btn adm-btn--default adm-btn--sm"
              onClick={() => fetchPosts(pagination.current_page - 1)}
              disabled={pagination.current_page <= 1}
            >
              ←
            </button>
            <span>{pagination.current_page} / {pagination.last_page}</span>
            <button
              className="adm-btn adm-btn--default adm-btn--sm"
              onClick={() => fetchPosts(pagination.current_page + 1)}
              disabled={pagination.current_page >= pagination.last_page}
            >
              →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
